import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, Youtube, Instagram, Facebook, Music2, Star } from 'lucide-react';
import { useSceneTime, seg, EASE } from '../engine/sceneTime';
import { Words, Fade, Eyebrow, Line } from '../components/Kinetic';
import { Vid, PhoneFrame } from '../components/Media';
import { CountUp, fmtBig } from '../components/CountUp';
import { getUKMap } from '../components/ukMap';
import { getTeamPhotos, loadTeamPhotos } from '../engine/teamPhotos';
import { PRESENTATION } from '../presentationConfig';

// ── 04 PEOPLE / UK MAP ──────────────────────────────────────────
function seeded(i) { const x = Math.sin(i * 9301 + 49297) * 233280; return x - Math.floor(x); }

// Coverage: England & Wales only (no Scotland / NI / far north). Headshots are weighted south → midlands → occasional north.
const SOUTH = new Set(['E', 'EC', 'N', 'NW', 'SE', 'SW', 'W', 'WC', 'AL', 'BR', 'CM', 'CR', 'DA', 'EN', 'GU', 'HA', 'HP', 'IG', 'KT', 'LU', 'ME', 'MK', 'OX', 'RG', 'RH', 'RM', 'SG', 'SL', 'SM', 'SS', 'TN', 'TW', 'UB', 'WD', 'CT', 'BN', 'PO', 'SO', 'SP', 'BA', 'BS', 'BH', 'DT', 'EX', 'TA', 'TQ', 'PL', 'TR', 'CB', 'CO', 'IP', 'NR', 'PE', 'SN', 'GL']);
const MIDLANDS = new Set(['B', 'CV', 'DE', 'DY', 'LE', 'NG', 'NN', 'ST', 'TF', 'WS', 'WV', 'SY', 'LN', 'HR', 'WR', 'CF', 'NP', 'CH', 'CW', 'SK']);
const NORTH = new Set(['M', 'L', 'LS', 'BD', 'HD', 'HX', 'WF', 'S', 'DN', 'HU', 'YO', 'PR', 'BB', 'BL', 'OL', 'WA', 'WN', 'FY', 'HG']);
const COVERED = new Set([...SOUTH, ...MIDLANDS, ...NORTH]);

function useTeamPhotos() {
  const [team, setTeam] = useState(getTeamPhotos);
  useEffect(() => { let alive = true; loadTeamPhotos().then((l) => alive && setTeam(l)); return () => { alive = false; }; }, []);
  return team;
}

export function People({ scene }) {
  const { t } = useSceneTime();
  const team = useTeamPhotos();
  const W = 640; const H = 860;
  const map = useMemo(() => getUKMap(W, H), []);
  const pins = useMemo(() => map.pins.filter((p) => COVERED.has(p.id)).map((p, i) => ({ ...p, at: 1.5 + seeded(i) * 15 })), [map]);
  const routes = useMemo(() => Array.from({ length: 9 }, (_, i) => {
    const a = pins[Math.floor(seeded(i + 100) * pins.length)];
    const b = pins[Math.floor(seeded(i + 200) * pins.length)];
    const mx = (a.x + b.x) / 2 + (seeded(i + 300) - 0.5) * 160;
    const my = (a.y + b.y) / 2 - 60 - seeded(i + 400) * 80;
    return { id: i, d: `M${a.x},${a.y} Q${mx},${my} ${b.x},${b.y}`, at: 3 + i * 1.6 };
  }), [pins]);
  // Real photographers pop up around the map — mostly south/orbital, then midlands, the north only now and then.
  const pops = useMemo(() => {
    if (!team.length) return [];
    const weighted = pins.flatMap((p) => Array(SOUTH.has(p.id) ? 4 : MIDLANDS.has(p.id) ? 2 : 1).fill(p));
    const recent = [];
    const farEnough = (p) => recent.every((r) => Math.hypot(r.x - p.x, r.y - p.y) >= 150);
    return Array.from({ length: team.length }, (_, i) => {
      let pin = weighted[Math.floor(seeded(i + 500) * weighted.length)];
      for (let guard = 0; guard < 60 && !farEnough(pin); guard += 1) pin = weighted[Math.floor(seeded(i * 50 + 600 + guard) * weighted.length)];
      recent.push(pin);
      if (recent.length > 4) recent.shift();
      const person = team[i];
      return { id: i, x: pin.x, y: pin.y, img: person.url, name: person.first_name, at: 2.2 + i * 1.1, dur: 3.4 + seeded(i + 700) * 1.2 };
    });
  }, [pins, team]);
  const litPins = pins.filter((p) => t >= p.at).length;
  return (
    <div className="pk-fill">
      <div className="pk-abs" style={{ left: 120, top: 170, width: 900 }}>
        <Eyebrow show={t >= 0.6}>The people behind it</Eyebrow>
        <Words text={scene.headline} show={t >= 1} size={100} lime={[0]} style={{ marginTop: 16 }} />
        <Fade show={t >= 5} y={18} className="pk-sub" style={{ marginTop: 36, fontSize: 40 }}>{scene.sub}</Fade>
        <Fade show={t >= 9} y={18} className="pk-glass pk-photographer-card" style={{ marginTop: 56 }}>
          <img src={scene.media.photographer.url} alt="" />
          <div>
            <div className="pk-stat-label">Employed, not freelance</div>
            <div className="pk-stat-value"><CountUp to={150} progress={seg(t, 9.2, 12)} /><span className="pk-lime">+</span></div>
            <div className="pk-stat-sub">visits counted live · <span>{litPins}</span> areas lit</div>
          </div>
        </Fade>
        <Fade show={t >= 16} y={10} className="pk-caption" style={{ marginTop: 40 }}>{scene.caption}</Fade>
      </div>
      <div className="pk-abs pk-map-wrap" style={{ left: 1130, top: 90, width: W, height: H }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="pk-map">
          <defs>
            <filter id="pk-glow"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          </defs>
          <motion.path d={map.d} fill="rgba(255,255,255,0.035)" stroke="rgba(150,193,31,0.75)" strokeWidth={1.6}
            initial={false} animate={{ pathLength: t >= 0.2 ? 1 : 0, opacity: 1 }} transition={{ duration: 3.2, ease: 'easeInOut' }} />
          {routes.map((r) => {
            const on = t >= r.at;
            return (
              <g key={r.id}>
                <motion.path d={r.d} fill="none" stroke="#96C11F" strokeWidth={2} strokeLinecap="round" filter="url(#pk-glow)"
                  initial={false} animate={{ pathLength: on ? 1 : 0, opacity: on ? 0.9 : 0 }} transition={{ duration: 2.4, ease: 'easeInOut' }} />
                {on && (
                  <circle r={5} fill="#fff" style={{ offsetPath: `path('${r.d}')`, animation: 'pk-travel 2.4s cubic-bezier(.4,0,.2,1) forwards' }} />
                )}
              </g>
            );
          })}
          {pins.map((p) => {
            const on = t >= p.at;
            return (
              <g key={p.id} transform={`translate(${p.x},${p.y})`}>
                <motion.circle r={14} fill="rgba(150,193,31,0.18)" initial={false} animate={{ scale: on ? [1, 1.9, 1] : 0, opacity: on ? 1 : 0 }} transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.4 }} />
                <motion.circle r={3.5} fill="#C6EE5A" initial={false} animate={{ scale: on ? 1 : 0, opacity: on ? 1 : 0 }} transition={{ duration: 0.5, ease: EASE }} />
              </g>
            );
          })}
        </svg>
        {pops.map((p) => {
          const on = t >= p.at && t < p.at + p.dur;
          return (
            <motion.div key={p.id} className="pk-headshot" style={{ left: p.x - 40, top: p.y - 104 }} initial={false}
              animate={{ opacity: on ? 1 : 0, scale: on ? 1 : 0.3, y: on ? 0 : 20 }}
              transition={on ? { type: 'spring', stiffness: 300, damping: 18 } : { duration: 0.4, ease: EASE }} data-testid={`pk-headshot-${p.id}`}>
              <img src={p.img} alt="" />
              <span className="pk-headshot-star"><Star size={12} fill="currentColor" /></span>
              <span className="pk-headshot-name">{p.name}</span>
              <i className="pk-headshot-stem" />
            </motion.div>
          );
        })}
        {routes.slice(0, 5).map((r, i) => (
          <motion.div key={r.id} className="pk-map-cam" style={{ left: 40 + i * 118, top: H - 40 }}
            initial={false} animate={{ opacity: t >= r.at ? 1 : 0, y: t >= r.at ? 0 : 12 }} transition={{ duration: 0.6 }}>
            <Camera size={14} /> Shoot {i + 1}
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ── 05 VIDEO ────────────────────────────────────────────────────
const PLATFORM_ICONS = { 'YouTube Shorts': Youtube, 'Instagram Reels': Instagram, Facebook, TikTok: Music2 };
const PHONE_W = 290;
const PHONE_X = [215, 615, 1015, 1415];

export function Video({ scene }) {
  const { t } = useSceneTime();
  const wordIn = t >= 0.3;
  const settle = t >= 11.5;
  return (
    <div className="pk-fill">
      <motion.div className="pk-abs pk-reveal-glow" style={{ left: 360, top: 260, width: 1200, height: 760 }} initial={false}
        animate={{ opacity: t >= 3 ? 0.7 : 0 }} transition={{ duration: 2 }} />
      <motion.div className="pk-abs" style={{ left: 0, width: 1920, textAlign: 'center' }} initial={false}
        animate={{ top: settle ? 60 : 120, opacity: wordIn ? 1 : 0 }} transition={{ duration: 1, ease: EASE }}>
        <Eyebrow show={t >= 1.4}>{scene.eyebrow}</Eyebrow>
        <motion.div className="pk-video-word" initial={false} animate={{ scale: settle ? 0.62 : 1, opacity: settle ? 0.55 : 1 }} transition={{ duration: 1, ease: EASE }}>
          {scene.word.split('').map((ch, i) => (
            <motion.span key={i} initial={false} animate={{ y: wordIn ? 0 : 120, opacity: wordIn ? 1 : 0, filter: wordIn ? 'blur(0px)' : 'blur(14px)' }} transition={{ duration: 0.9, delay: i * 0.07, ease: EASE }}>{ch}</motion.span>
          ))}
        </motion.div>
        <Line show={t >= 1.6} width={180} origin="center" style={{ margin: '14px auto 0' }} className="pk-line-glow" />
      </motion.div>
      {scene.media.feeds.map((v, i) => {
        const on = t >= 3.2 + i * 0.5;
        const Icon = PLATFORM_ICONS[scene.platforms[i]] || Youtube;
        return (
          <motion.div key={i} className="pk-abs pk-video-phone" style={{ left: PHONE_X[i], top: 380, width: PHONE_W }}
            initial={false}
            animate={{ opacity: on ? 1 : 0, y: on ? (settle ? -70 : 0) : 420, scale: settle ? 0.78 : 1 }}
            transition={{ opacity: { duration: 0.7 }, scale: { duration: 1.1, ease: EASE }, y: { duration: 1.1, ease: EASE } }}>
            <PhoneFrame width={PHONE_W} glow>
              <Vid video={v} className="pk-fill" active={on} />
            </PhoneFrame>
            <motion.div className="pk-platform-wrap" initial={false} animate={{ opacity: t >= 5.2 + i * 0.4 ? 1 : 0, y: t >= 5.2 + i * 0.4 ? 0 : 10 }} transition={{ duration: 0.6 }}>
              <span className="pk-platform"><Icon size={18} /> {scene.platforms[i]}</span>
            </motion.div>
          </motion.div>
        );
      })}
      <div className="pk-abs" style={{ left: 0, top: 920, width: 1920, textAlign: 'center' }}>
        <Words text={scene.headline} show={settle} size={84} className="pk-words-center" lime={[3]} nowrap />
      </div>
    </div>
  );
}

// ── 06 SCALE OF VIDEO ───────────────────────────────────────────
const DEPTH = Array.from({ length: 48 }, (_, i) => ({
  x: ((i * 613) % 1700) + 110, y: ((i * 389) % 820) + 130, z: -((i * 97) % 1800) - 300, d: 2 + (i % 11) * 0.6,
}));

export function ScaleOfVideo({ scene }) {
  const { t } = useSceneTime();
  const words = scene.punch.split(' ');
  return (
    <div className="pk-fill">
      <div className="pk-abs pk-depth" style={{ left: 0, top: 0, width: 1920, height: 1080 }}>
        {DEPTH.map((p, i) => {
          const on = t >= p.d;
          return (
            <motion.div key={i} className="pk-depth-phone" style={{ left: p.x, top: p.y }}
              initial={false}
              animate={{ opacity: on ? 0.55 : 0, transform: on ? `translateZ(${p.z}px)` : `translateZ(${p.z - 600}px)` }}
              transition={{ duration: 1.4, ease: EASE }} />
          );
        })}
      </div>
      <div className="pk-abs" style={{ left: 120, top: 170, width: 1680 }}>
        <Words text={scene.headline} show={t >= 0.5} size={86} lime={[0]} nowrap />
        <div className="pk-punch">
          {words.map((w, i) => (
            <motion.span key={i} initial={false} animate={{ opacity: t >= 3.2 + i * 0.9 ? 1 : 0, scale: t >= 3.2 + i * 0.9 ? 1 : 1.5 }} transition={{ duration: 0.45, ease: EASE }}>{w}</motion.span>
          ))}
        </div>
      </div>
      <div className="pk-abs pk-stat-row" style={{ left: 120, top: 560, width: 1680 }}>
        {scene.stats.map((s, i) => {
          const start = s.total ? 12 : 6.5 + i * 2.5;
          const on = t >= start;
          return (
            <motion.div key={s.label} className={`pk-glass pk-glass-active pk-stat-card ${s.total ? 'pk-stat-total' : ''}`} initial={false}
              animate={{ opacity: on ? 1 : 0, y: on ? 0 : 60, scale: on ? 1 : 0.96 }} transition={{ duration: 0.9, ease: EASE }} data-testid={`pk-stat-${i}`}>
              <div className="pk-stat-label">{s.label}</div>
              <div className="pk-stat-big">
                <CountUp to={s.value} progress={seg(t, start + 0.3, start + (s.total ? 2.4 : 4.5))} format={fmtBig} />
                {s.suffix && <span className="pk-stat-suffix">{s.suffix}</span>}
              </div>
              <div className="pk-stat-sub">{s.sub}</div>
              {!s.verified && PRESENTATION.showUnverifiedBadge && <div className="pk-verify">to verify</div>}
            </motion.div>
          );
        })}
      </div>
      <Fade show={t >= 15.5} y={12} className="pk-caption" style={{ position: 'absolute', left: 120, top: 960 }}>
        <span className="pk-lime">340 billion</span> short-form views a day — the default way people now meet a home.
      </Fade>
    </div>
  );
}
